/**
 * Msg 563 · D.5 — Bulk Import page.
 *
 * Admin-only route `/atlas/bulk-import`. Advisor admin pastes lines or
 * uploads a CSV / JSON, previews each row's proposed placement via
 * `POST /api/atlas/topics/{root}/suggest-node`, edits or skips rows,
 * then submits the batch to `POST /api/atlas/knowledge/bulk-import`.
 */
import React, { useMemo, useRef, useState } from "react";
import {
  Upload, ClipboardPaste, Send, Loader2, Check, X as CloseIcon,
  ChevronRight, Sparkles, ArrowUpRight, AlertTriangle, Plus, Pencil,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

import { Chip, humanize } from "./AtlasTopicShared";


function parseCsvOrJson(input, filename) {
  const text = (input || "").trim();
  if (!text) return [];
  // JSON array?
  if (text.startsWith("[")) {
    try {
      const arr = JSON.parse(text);
      if (Array.isArray(arr)) {
        return arr.map((r) => ({
          text: String(r.text || "").trim(),
          topic_slug: r.topic_slug || r.slug || "",
          display_type: r.display_type || "advisor_tip",
        })).filter((r) => r.text);
      }
    } catch { /* fall through to CSV */ }
  }
  // CSV (with header) — accept `text,topic_slug,display_type`.
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];
  // Header detection: if first line contains "text" as a column
  // header treat it as CSV; otherwise treat every line as a plain
  // text row.
  const first = lines[0].toLowerCase();
  if (first.includes("text") && (first.includes(",") || first.includes(";"))) {
    const sep = first.includes(";") ? ";" : ",";
    const hdr = lines[0].split(sep).map((s) => s.trim().toLowerCase());
    const iText = hdr.indexOf("text");
    const iSlug = hdr.indexOf("topic_slug");
    const iType = hdr.indexOf("display_type");
    const out = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(sep).map((s) => s.trim());
      const t = (cols[iText] || "").trim();
      if (!t) continue;
      out.push({
        text: t,
        topic_slug: iSlug >= 0 ? (cols[iSlug] || "") : "",
        display_type: iType >= 0 ? (cols[iType] || "advisor_tip") : "advisor_tip",
      });
    }
    return out;
  }
  // Plain lines (one row per line).
  return lines.map((l) => ({
    text: l, topic_slug: "", display_type: "advisor_tip",
  }));
}


function PathPreview({ path }) {
  if (!path || path.length === 0) {
    return (
      <span
        data-testid="bulk-row-path-preview"
        className="font-hand text-tb-navy/40 text-xs"
      >
        (root only)
      </span>
    );
  }
  return (
    <span className="inline-flex items-center flex-wrap gap-1"
      data-testid="bulk-row-path-preview">
      {path.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 && <ChevronRight className="w-3 h-3 text-tb-navy/40" />}
          <span className={`px-1.5 py-0.5 rounded font-hand text-[11px] ${
            p.exists ? "bg-tb-navy/5 text-tb-navy" : "bg-amber-100 text-amber-800"
          }`}>
            {p.name}
            {!p.exists && (
              <span className="ml-1 text-[9px] uppercase tracking-widest">new</span>
            )}
          </span>
        </React.Fragment>
      ))}
    </span>
  );
}


function PathEditorInline({ initialPath, onSave, onCancel }) {
  const [rows, setRows] = useState((initialPath || []).map((p) => ({ ...p })));
  const commit = () => {
    const cleaned = rows
      .map((r) => ({
        slug: (r.slug || r.name || "").toLowerCase().trim().replace(/\s+/g, "-"),
        name: (r.name || "").trim() || r.slug || "",
        category: r.category || "other",
        entity_type: r.entity_type || "other",
        exists: !!r.exists,
      }))
      .filter((r) => r.slug);
    onSave(cleaned);
  };
  return (
    <div className="mt-2 p-2 rounded-lg border border-tb-navy/20 bg-tb-navy/[0.02]"
         data-testid="bulk-row-path-editor">
      <div className="space-y-1.5">
        {rows.map((r, idx) => (
          <div key={idx} className="flex items-center gap-1.5">
            <span className="font-hand text-[10px] text-tb-navy/50 w-3">{idx + 1}</span>
            <input value={r.name || ""}
              onChange={(e) => setRows((prev) => prev.map((p, i) => i === idx ? { ...p, name: e.target.value } : p))}
              placeholder="Step name"
              className="flex-1 h-8 px-2 rounded border border-tb-navy/25 font-hand text-xs text-tb-navy" />
            <button type="button"
              onClick={() => setRows((prev) => prev.filter((_, i) => i !== idx))}
              className="h-7 w-7 inline-flex items-center justify-center rounded text-tb-navy/50 hover:bg-tb-navy/5">
              <CloseIcon className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <button type="button"
          onClick={() => setRows((prev) => [...prev, { slug: "", name: "", category: "other", entity_type: "other", exists: false }])}
          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-dashed border-tb-navy/25 text-tb-navy/70 font-hand text-[11px]">
          <Plus className="w-3 h-3" /> Add step
        </button>
        <div className="flex-1" />
        <button type="button" onClick={onCancel}
          className="h-7 px-2.5 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-[11px]">
          Cancel
        </button>
        <button type="button" onClick={commit}
          data-testid="bulk-row-path-save"
          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-[11px]">
          <Check className="w-3 h-3" /> Save
        </button>
      </div>
    </div>
  );
}


function PreviewRow({ idx, row, onEditPath, onSkip, onUnskip }) {
  const [editing, setEditing] = useState(false);
  const savePath = (next) => { onEditPath(idx, next); setEditing(false); };
  const conf = Math.round((row.confidence || 0) * 100);
  return (
    <tr
      className={`border-t border-tb-navy/10 ${row.skipped ? "opacity-40" : ""}`}
      data-testid={`bulk-preview-row-${idx}`}
    >
      <td className="px-3 py-2 font-hand text-[11px] text-tb-navy/60 w-8">{idx + 1}</td>
      <td className="px-3 py-2 font-hand text-sm text-tb-navy">
        <div className="max-w-md">
          {row.text}
        </div>
        <div className="mt-1 text-[10px] font-hand text-tb-navy/40 uppercase tracking-widest">
          Root: <b>{row.root_slug || "—"}</b>
        </div>
      </td>
      <td className="px-3 py-2">
        {row.status === "analyzing" && (
          <span className="inline-flex items-center gap-1 font-hand text-xs text-tb-navy/50">
            <Loader2 className="w-3 h-3 animate-spin" /> analyzing…
          </span>
        )}
        {row.status === "error" && (
          <span className="inline-flex items-center gap-1 font-hand text-xs text-red-700"
                data-testid={`bulk-preview-row-error-${idx}`}>
            <AlertTriangle className="w-3 h-3" /> {row.error || "error"}
          </span>
        )}
        {row.status !== "analyzing" && row.status !== "error" && !editing && (
          <PathPreview path={row.suggested_path || []} />
        )}
        {editing && (
          <PathEditorInline
            initialPath={row.suggested_path || []}
            onSave={savePath}
            onCancel={() => setEditing(false)}
          />
        )}
      </td>
      <td className="px-3 py-2 font-hand text-xs text-tb-navy/60">
        {row.status === "ready" ? `${conf}%` : ""}
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {row.status === "ready" && !row.skipped && !editing && (
          <>
            <button type="button" onClick={() => setEditing(true)}
              data-testid={`bulk-row-edit-${idx}`}
              className="h-7 px-2 inline-flex items-center gap-1 rounded text-tb-navy/70 hover:bg-tb-navy/5 font-hand text-[11px]">
              <Pencil className="w-3 h-3" /> Edit
            </button>
            <button type="button" onClick={() => onSkip(idx)}
              data-testid={`bulk-row-skip-${idx}`}
              className="h-7 px-2 inline-flex items-center gap-1 rounded text-red-700/80 hover:bg-red-50 font-hand text-[11px]">
              <CloseIcon className="w-3 h-3" /> Skip
            </button>
          </>
        )}
        {row.skipped && (
          <button type="button" onClick={() => onUnskip(idx)}
            data-testid={`bulk-row-unskip-${idx}`}
            className="h-7 px-2 inline-flex items-center gap-1 rounded text-tb-navy/70 hover:bg-tb-navy/5 font-hand text-[11px]">
            Unskip
          </button>
        )}
      </td>
    </tr>
  );
}


function SubmitReport({ report }) {
  if (!report) return null;
  const passed = report.results || [];
  const failed = report.failed_rows || [];
  return (
    <div className="mt-6" data-testid="bulk-submit-report">
      <h3 className="text-tb-navy font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
        Batch result
      </h3>
      <p className="mt-1 font-hand text-tb-navy/60 text-xs">
        Batch <b>{report.batch_id}</b> — <b>{passed.length}</b> filed,{" "}
        <b>{failed.length}</b> failed.
      </p>
      <ul className="mt-3 space-y-1">
        {passed.map((r, i) => (
          <li key={`ok-${i}`} data-testid={`bulk-report-ok-${r.index}`}
              className="font-hand text-xs text-emerald-700 flex items-center gap-1.5">
            <Check className="w-3 h-3" />
            Row #{r.index + 1} — filed at{" "}
            <a href={`/atlas/topics/${r.deepest_slug}`} className="underline underline-offset-2">
              {r.deepest_slug}
            </a>
            {r.created_slugs?.length > 0 && (
              <span className="text-tb-navy/50">
                (created: {r.created_slugs.join(", ")})
              </span>
            )}
          </li>
        ))}
        {failed.map((r, i) => (
          <li key={`err-${i}`} data-testid={`bulk-report-fail-${r.index}`}
              className="font-hand text-xs text-red-700 flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" />
            Row #{r.index + 1} — {r.error}
          </li>
        ))}
      </ul>
    </div>
  );
}


export default function AtlasBulkImportPage() {
  const [tab, setTab] = useState("paste");
  const [textInput, setTextInput] = useState("");
  const [defaultRoot, setDefaultRoot] = useState("");
  const [rows, setRows] = useState([]); // {text, root_slug, suggested_path, confidence, status, skipped, error}
  const [analyzing, setAnalyzing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState(null);
  const fileRef = useRef(null);

  const parsedFromInput = useMemo(() => parseCsvOrJson(textInput), [textInput]);
  const readyCount = rows.filter((r) => r.status === "ready" && !r.skipped).length;

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const text = await f.text();
    setTextInput(text);
    setTab("paste");
    toast.success(`Loaded ${f.name}`);
  };

  const analyze = async () => {
    if (parsedFromInput.length === 0) {
      toast.error("Nothing to analyze — paste some lines first.");
      return;
    }
    const initial = parsedFromInput.map((r) => ({
      text: r.text,
      root_slug: (r.topic_slug || defaultRoot || "").trim(),
      display_type: r.display_type || "advisor_tip",
      suggested_path: [],
      confidence: 0,
      status: "analyzing",
      skipped: false,
      error: null,
    }));
    setRows(initial);
    setReport(null);
    setAnalyzing(true);
    // Serially call suggest-node per row.
    for (let i = 0; i < initial.length; i++) {
      const r = initial[i];
      if (!r.root_slug) {
        setRows((prev) => prev.map((p, idx) => idx === i
          ? { ...p, status: "error", error: "No root topic — set a default at top or add topic_slug column" }
          : p));
        continue;
      }
      try {
        const resp = await api.post(`/atlas/topics/${r.root_slug}/suggest-node`, { text: r.text });
        const path = resp.data?.suggested_path || [];
        const conf = Number(resp.data?.confidence || 0);
        const ok = resp.data?.llm_available !== false;
        setRows((prev) => prev.map((p, idx) => idx === i
          ? { ...p, suggested_path: path, confidence: conf,
              status: "ready",
              error: ok ? null : "LLM unavailable — will file at root only" }
          : p));
      } catch (err) {
        setRows((prev) => prev.map((p, idx) => idx === i
          ? { ...p, status: "error", error: formatApiErrorDetail(err?.response?.data?.detail) || "network error" }
          : p));
      }
    }
    setAnalyzing(false);
  };

  const editPath = (idx, newPath) => {
    setRows((prev) => prev.map((p, i) => i === idx
      ? { ...p, suggested_path: newPath } : p));
  };
  const skipRow = (idx) => {
    setRows((prev) => prev.map((p, i) => i === idx ? { ...p, skipped: true } : p));
  };
  const unskipRow = (idx) => {
    setRows((prev) => prev.map((p, i) => i === idx ? { ...p, skipped: false } : p));
  };

  const submit = async () => {
    const toSubmit = rows
      .map((r, idx) => ({ ...r, __idx: idx }))
      .filter((r) => r.status === "ready" && !r.skipped);
    if (toSubmit.length === 0) {
      toast.error("Nothing ready to submit.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/atlas/knowledge/bulk-import", {
        rows: toSubmit.map((row) => ({
          text: row.text,
          root_slug: row.root_slug,
          display_type: row.display_type || "advisor_tip",
          suggested_path: (row.suggested_path || []).map((p) => ({
            slug: p.slug, name: p.name,
            category: p.category, entity_type: p.entity_type,
            exists: !!p.exists,
          })),
        })),
      });
      setReport(r.data);
      toast.success(`Filed ${r.data.success_count} of ${toSubmit.length} rows.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Bulk-import failed.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl" data-testid="atlas-bulk-import-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-tb-navy text-3xl md:text-4xl font-semibold"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            Bulk import
          </h1>
          <p className="mt-1 font-hand text-tb-navy/60 text-sm max-w-2xl">
            Batch add advisor facts to Topics. Paste plain lines or upload a CSV / JSON.
            Every row is run through the LLM sub-node suggester rooted at a topic you pick,
            previewed with proposed placement, and only committed on Submit.
          </p>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-2">
        <button type="button" onClick={() => setTab("paste")}
          data-testid="bulk-tab-paste"
          className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full font-hand text-sm ${
            tab === "paste"
              ? "bg-tb-navy text-tb-gold-bright"
              : "border border-tb-navy/20 text-tb-navy hover:bg-tb-navy/5"
          }`}>
          <ClipboardPaste className="w-3.5 h-3.5" /> Paste
        </button>
        <button type="button" onClick={() => setTab("upload")}
          data-testid="bulk-tab-upload"
          className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full font-hand text-sm ${
            tab === "upload"
              ? "bg-tb-navy text-tb-gold-bright"
              : "border border-tb-navy/20 text-tb-navy hover:bg-tb-navy/5"
          }`}>
          <Upload className="w-3.5 h-3.5" /> Upload CSV/JSON
        </button>
      </div>

      <div className="mt-4">
        <label className="block">
          <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">
            Default root topic (slug — used when a row doesn't specify one)
          </span>
          <input value={defaultRoot} onChange={(e) => setDefaultRoot(e.target.value)}
            placeholder="e.g. aruba, cruise-lines, wonder-of-the-seas…"
            data-testid="bulk-default-root"
            className="mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 outline-none font-hand text-sm bg-white text-tb-navy" />
        </label>
      </div>

      {tab === "paste" && (
        <div className="mt-4">
          <textarea
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder="Paste one fact per line, or paste CSV/JSON…"
            data-testid="bulk-paste-textarea"
            rows={8}
            className="w-full px-3 py-2 rounded-xl border border-tb-navy/20 outline-none focus:border-tb-navy font-hand text-sm bg-white text-tb-navy resize-y"
          />
          <p className="mt-1 font-hand text-[10px] text-tb-navy/50">
            {parsedFromInput.length} row{parsedFromInput.length === 1 ? "" : "s"} detected.
          </p>
        </div>
      )}

      {tab === "upload" && (
        <div className="mt-4">
          <label className="block cursor-pointer">
            <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Upload</span>
            <div
              className="mt-1 rounded-xl border-2 border-dashed border-tb-navy/25 bg-white p-8 text-center hover:border-tb-navy transition"
              onClick={() => fileRef.current?.click()}
              data-testid="bulk-upload-dropzone"
            >
              <Upload className="w-6 h-6 text-tb-navy/40 mx-auto" />
              <p className="mt-2 font-hand text-sm text-tb-navy">Click to choose a CSV or JSON file</p>
              <p className="mt-1 font-hand text-[11px] text-tb-navy/50">
                CSV header: <code>text,topic_slug,display_type</code> — JSON: array of objects
              </p>
            </div>
          </label>
          <input ref={fileRef} type="file" accept=".csv,.json,text/csv,application/json"
            onChange={onFile} className="hidden"
            data-testid="bulk-file-input" />
          {textInput && (
            <p className="mt-2 font-hand text-[10px] text-tb-navy/50">
              Loaded {parsedFromInput.length} row{parsedFromInput.length === 1 ? "" : "s"}.
            </p>
          )}
        </div>
      )}

      <div className="mt-5 flex items-center gap-2">
        <button type="button" onClick={analyze} disabled={analyzing || parsedFromInput.length === 0}
          data-testid="bulk-analyze"
          className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-50">
          {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Analyze
        </button>
        {rows.length > 0 && (
          <span className="font-hand text-[11px] text-tb-navy/60">
            {readyCount} of {rows.length} ready
          </span>
        )}
      </div>

      {rows.length > 0 && (
        <div className="mt-6 overflow-hidden rounded-xl border border-tb-navy/15">
          <table className="w-full" data-testid="bulk-preview-table">
            <thead className="bg-tb-navy/[0.04]">
              <tr>
                <th className="text-left px-3 py-2 font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 w-8">#</th>
                <th className="text-left px-3 py-2 font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Text</th>
                <th className="text-left px-3 py-2 font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Proposed placement</th>
                <th className="text-left px-3 py-2 font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 w-16">Conf</th>
                <th className="text-right px-3 py-2 font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 w-28">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <PreviewRow key={idx} idx={idx} row={r}
                  onEditPath={editPath}
                  onSkip={skipRow}
                  onUnskip={unskipRow}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-5 flex items-center gap-2">
          <button type="button" onClick={submit}
            disabled={submitting || readyCount === 0}
            data-testid="bulk-submit"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-50">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Submit {readyCount} row{readyCount === 1 ? "" : "s"}
          </button>
        </div>
      )}

      <SubmitReport report={report} />
    </div>
  );
}
